import { useState } from 'react';
import type { DestinoValorResponse, RateioMapaColuna, RateioMapaLinha } from '@triade/contracts';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { ApiError } from '@/lib/api';
import { ehZero, formatarFracaoPercentual, formatarMoeda } from '@/lib/format';
import { cn } from '@/lib/utils';

/** Mensagem da API (texto + `code`) para alertas. Falha de rede vira texto generico. */
export function mensagemApi(e: unknown): string {
  if (e instanceof ApiError) return `${e.message}${e.code ? ` (código ${e.code})` : ''}`;
  return 'Falha de comunicação com a API.';
}

export type TomCard = 'sucesso' | 'alerta' | 'erro' | 'neutro';

const TOM: Record<TomCard, { faixa: string; fundo: string }> = {
  sucesso: { faixa: 'bg-success-ink', fundo: 'bg-success-bg text-success-ink' },
  alerta: { faixa: 'bg-warning-ink', fundo: 'bg-warning-bg text-warning-ink' },
  erro: { faixa: 'bg-danger-ink', fundo: 'bg-danger-bg text-danger-ink' },
  neutro: { faixa: 'bg-line-strong', fundo: 'bg-card text-ink-body' },
};

/** Card de total: faixa colorida + rotulo + valor. Valor ja vem formatado pela tela (o front nao calcula). */
export function CardTotal({ rotulo, valor, tom = 'neutro', testid }: { rotulo: string; valor: string; tom?: TomCard; testid?: string }) {
  return (
    <div data-testid={testid} className={cn('flex min-w-[200px] flex-1 items-stretch overflow-hidden rounded-xl border border-border', TOM[tom].fundo)}>
      <div aria-hidden="true" className={cn('w-2 shrink-0', TOM[tom].faixa)} />
      <div className="flex flex-col gap-0.5 px-4 py-3">
        <span className="tabular text-title font-semibold">{valor}</span>
        <span className="text-small opacity-90">{rotulo}</span>
      </div>
    </div>
  );
}

/** Estado de expansao das linhas (ids abertos). */
export function useExpansao() {
  const [abertas, setAbertas] = useState<ReadonlySet<string>>(() => new Set());
  return {
    aberta: (id: string) => abertas.has(id),
    alternar: (id: string) =>
      setAbertas((atual) => {
        const proximo = new Set(atual);
        if (proximo.has(id)) proximo.delete(id);
        else proximo.add(id);
        return proximo;
      }),
  };
}

/** Botao +/− da linha expansivel (aria-expanded). */
export function BotaoExpandir({ aberta, rotulo, onClick }: { aberta: boolean; rotulo: string; onClick: () => void }) {
  const Icone = aberta ? ChevronDown : ChevronRight;
  return (
    <button
      type="button"
      aria-expanded={aberta}
      aria-label={`${aberta ? 'Recolher' : 'Expandir'} ${rotulo}`}
      onClick={onClick}
      className="flex size-6 items-center justify-center rounded-md text-ink-secondary hover:bg-line-soft"
    >
      <Icone aria-hidden="true" className="size-4" />
    </button>
  );
}

/** Tabela de destinos (centro, percentual, valor). Coluna de R$ so aparece se a API mandou `valor`. */
export function TabelaDestinos({ destinos, rotulo }: { destinos: DestinoValorResponse[]; rotulo: string }) {
  const comValor = destinos.some((d) => d.valor !== undefined);
  return (
    <table className="w-full border-collapse text-small" aria-label={`Destinos de ${rotulo}`}>
      <thead>
        <tr className="text-left text-caption text-ink-muted">
          <th scope="col" className="px-3 py-2 font-medium">Centro de custo destino</th>
          <th scope="col" className="px-3 py-2 font-medium">Rate</th>
          {comValor && <th scope="col" className="px-3 py-2 font-medium">Valor</th>}
        </tr>
      </thead>
      <tbody>
        {destinos.map((d, i) => (
          <tr key={`${d.centroId ?? 'nao-alocado'}-${i}`} className="border-t border-line-soft">
            <td className="px-3 py-2">{d.centroNome}</td>
            <td className="tabular px-3 py-2 text-ink-secondary">{formatarFracaoPercentual(d.percentual)}</td>
            {comValor && <td className="tabular px-3 py-2 text-ink-secondary">{formatarMoeda(d.valor)}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Total por centro de uma linha do mapa (Salarios ou Depreciacoes): so os centros com valor diferente de zero e a linha "Total geral"
 * com o total que a API mandou. Sem `celulas` na linha (sem custo.ver ou sem rh.ver) nao renderiza nada.
 */
export function TotalPorCentro({ linha, colunas, testid }: { linha: RateioMapaLinha | undefined; colunas: RateioMapaColuna[]; testid: string }) {
  if (!linha?.celulas || linha.total === undefined) return null;
  const celulas = linha.celulas;
  const comValor = colunas.filter((c) => {
    const valor = celulas[c.id];
    return valor !== undefined && valor !== '' && !ehZero(valor);
  });
  return (
    <section className="flex flex-col gap-2">
      <p className="text-small font-semibold">Total por centro</p>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full min-w-[320px] border-collapse text-small" aria-label="Total por centro">
          <thead>
            <tr className="bg-surface-muted text-left text-caption text-ink-muted">
              <th scope="col" className={TH}>Centro</th>
              <th scope="col" className={`${TH} text-right`}>Valor</th>
            </tr>
          </thead>
          <tbody>
            {comValor.map((c) => (
              <tr key={c.id} className="border-t border-line-soft">
                <td className={TD}>{c.nome}</td>
                <td className={`${TD} ${NUMERO}`}>{formatarMoeda(celulas[c.id])}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-border font-semibold">
              <td className={TD}>Total geral</td>
              <td className={`${TD} tabular text-right`} data-testid={testid}>{formatarMoeda(linha.total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

/** Cabecalho de coluna de tabela de rateio. */
export const TH = 'px-3 py-2.5 font-medium';
/** Celula de tabela de rateio. */
export const TD = 'px-3 py-3';
/** Tabelas de valores (CIFs, Salarios, Depreciacoes, Mapa): cabecalho sem quebra e padding compacto. */
export const TH_VALOR = 'whitespace-nowrap px-2.5 py-2 font-medium';
/** Celula de texto das tabelas de valores (quebra de linha permitida). */
export const TD_VALOR = 'px-2.5 py-2';
/** Celula numerica: digitos tabulares, alinhada a direita, valor sem quebra de linha ("R$" junto do numero). */
export const NUMERO = 'tabular whitespace-nowrap text-right';
